import { useState } from "react";
import {
  AlertCircle,
  Calendar as CalendarIcon,
  CalendarPlus,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  MapPin,
  Settings,
  Ticket,
  User,
  XCircle,
} from "lucide-react";
import type { BookingLinks } from "../../links";
import type { BundleBooking, ManageLocation, ManageTutor } from "./types";
import {
  bookingDate,
  calendarUrl,
  formatDateTime,
  isBookingCancelled,
  isStartingSoon,
  locationName,
  relativeTime,
  ticketCount,
  tutorName,
} from "./bundleManage";

// The bundle page's Upcoming and Past tabs (v1's UpcomingBookings and
// PastBookings): each booking made with the bundle, linking to its manage
// page.

type ListProps = {
  bookings: BundleBooking[];
  locations?: ManageLocation[];
  tutors?: ManageTutor[];
  links: BookingLinks;
  /** The clock the rows are dated on (the listing's, else the school's). */
  timezoneFor: (booking: BundleBooking) => string;
  now: number;
};

const chip = "inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium whitespace-nowrap";
const smallButton =
  "inline-flex h-7 items-center justify-center gap-1 rounded-md border bg-background px-2 text-xs font-medium shadow-sm hover:bg-muted";

function TicketsChip({ count }: { count: number }) {
  if (count <= 1) return null;
  return (
    <span className={`${chip} bg-purple-100 text-purple-700`}>
      <Ticket className="h-3 w-3" aria-hidden />
      {count} tickets
    </span>
  );
}

function CancelledChip() {
  return (
    <span className={`${chip} bg-red-100 text-red-700`}>
      <XCircle className="h-3 w-3" aria-hidden />
      Cancelled
    </span>
  );
}

function ManageLink({ href }: { href: string }) {
  return (
    <a href={href} className={smallButton}>
      <Settings className="h-3 w-3" aria-hidden />
      Manage
    </a>
  );
}

export function UpcomingBookings({ bookings, locations, tutors, links, timezoneFor, now }: ListProps) {
  if (bookings.length === 0) {
    return (
      <div className="py-12 text-center text-muted-foreground">
        <Clock className="mx-auto mb-3 h-12 w-12 opacity-30" aria-hidden />
        <p className="mb-1 text-base font-medium">No upcoming sessions booked</p>
        <p className="text-sm">Book sessions from the Services available or Timetable tab</p>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        You have {bookings.length} upcoming session{bookings.length > 1 ? "s" : ""} booked
      </p>
      <ul className="space-y-3">
        {bookings.map((booking) => {
          const date = bookingDate(booking);
          const cancelled = isBookingCancelled(booking);
          const soon = !!date && isStartingSoon(date, now);
          const where = locationName(locations, booking.session?.location || booking.course?.location);
          const tutor = tutorName(tutors, booking.session?.tutor);
          const calendar = cancelled ? undefined : calendarUrl(booking, where);
          const tone = cancelled
            ? "border-red-300 bg-red-50/40 opacity-75"
            : soon
              ? "border-orange-300 bg-orange-50/40"
              : "border-border";
          return (
            <li key={booking._id} className={`flex items-start gap-3 rounded-lg border p-4 ${tone}`}>
              <div
                className={`mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                  cancelled ? "bg-red-100 text-red-600" : soon ? "bg-orange-100 text-orange-600" : "bg-blue-100 text-blue-600"
                }`}
                aria-hidden
              >
                {cancelled ? <XCircle className="h-5 w-5" /> : <Clock className="h-5 w-5" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  <h4 className="font-medium">{booking.course?.title || "Unknown class"}</h4>
                  <TicketsChip count={ticketCount(booking)} />
                  {cancelled && <CancelledChip />}
                  {!cancelled && soon && (
                    <span className={`${chip} bg-orange-100 text-orange-700`}>
                      <AlertCircle className="h-3 w-3" aria-hidden />
                      Starting soon
                    </span>
                  )}
                </div>
                {date && (
                  <>
                    <p className="mb-1 flex items-center gap-2 text-sm font-medium text-blue-700">
                      <Clock className="h-3 w-3" aria-hidden />
                      {relativeTime(date, now, timezoneFor(booking))}
                    </p>
                    <p className="mb-1 flex items-center gap-1 text-sm text-muted-foreground">
                      <CalendarIcon className="h-3 w-3" aria-hidden />
                      {formatDateTime(date, timezoneFor(booking))}
                    </p>
                  </>
                )}
                {where && (
                  <p className="mb-1 flex items-center gap-1 text-sm text-muted-foreground">
                    <MapPin className="h-3 w-3" aria-hidden />
                    {where}
                  </p>
                )}
                {tutor && (
                  <p className="flex items-center gap-1 text-sm text-muted-foreground">
                    <User className="h-3 w-3" aria-hidden />
                    with {tutor}
                  </p>
                )}
              </div>
              <div className="flex shrink-0 flex-col gap-2">
                {calendar && (
                  <a href={calendar} target="_blank" rel="noopener noreferrer" className={smallButton} aria-label="Add to Google Calendar">
                    <CalendarPlus className="h-3 w-3" aria-hidden />
                  </a>
                )}
                <ManageLink href={links.manage(booking._id)} />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

const PAST_PAGE = 5;

export function PastBookings({ bookings, tutors, links, timezoneFor }: ListProps) {
  const [showAll, setShowAll] = useState(false);
  if (bookings.length === 0) {
    return (
      <div className="py-12 text-center text-muted-foreground">
        <CheckCircle2 className="mx-auto mb-3 h-12 w-12 opacity-30" aria-hidden />
        <p className="mb-1 text-base font-medium">No past sessions yet</p>
        <p className="text-sm">Your completed sessions will appear here</p>
      </div>
    );
  }
  const shown = showAll ? bookings : bookings.slice(0, PAST_PAGE);
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          You have {bookings.length} completed session{bookings.length > 1 ? "s" : ""}
        </p>
        {bookings.length > PAST_PAGE && (
          <span className="text-xs text-muted-foreground">{showAll ? "Showing all" : `Showing ${PAST_PAGE}`}</span>
        )}
      </div>
      <ul className="space-y-3">
        {shown.map((booking) => {
          const date = bookingDate(booking);
          const cancelled = isBookingCancelled(booking);
          const tutor = tutorName(tutors, booking.session?.tutor);
          return (
            <li
              key={booking._id}
              className={`flex items-start gap-3 rounded-lg border p-4 ${cancelled ? "border-red-300 bg-red-50/40 opacity-75" : "border-border"}`}
            >
              <div
                className={`mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                  cancelled ? "bg-red-100 text-red-600" : "bg-green-100 text-green-600"
                }`}
                aria-hidden
              >
                {cancelled ? <XCircle className="h-5 w-5" /> : <CheckCircle2 className="h-5 w-5" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  <h4 className="font-medium">{booking.course?.title || "Unknown class"}</h4>
                  <TicketsChip count={ticketCount(booking)} />
                  {cancelled && <CancelledChip />}
                </div>
                {date && (
                  <p className="mb-1 flex items-center gap-1 text-sm text-muted-foreground">
                    <CalendarIcon className="h-3 w-3" aria-hidden />
                    {formatDateTime(date, timezoneFor(booking))}
                  </p>
                )}
                {tutor && (
                  <p className="flex items-center gap-1 text-sm text-muted-foreground">
                    <User className="h-3 w-3" aria-hidden />
                    with {tutor}
                  </p>
                )}
              </div>
              <div className="flex shrink-0 flex-col items-end gap-2">
                {!cancelled && <span className={`${chip} bg-green-100 text-green-700`}>Completed</span>}
                <ManageLink href={links.manage(booking._id)} />
              </div>
            </li>
          );
        })}
      </ul>
      {bookings.length > PAST_PAGE && (
        <div className="text-center">
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            {showAll ? (
              <>
                <ChevronUp className="h-4 w-4" aria-hidden />
                Show less
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4" aria-hidden />
                Show {bookings.length - PAST_PAGE} more
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
